import { useGetMeProfile } from '@cocoonspace/sdk-js/v2/domains/me-profile/me-profile'
import { useCopyClipboard } from '@cocoonspace/shared/hooks/use-copy-clipboard.hook'
import { MobileDrawer } from '@cocoonspace/ui/components/cocoon/mobile-drawer'
import { SheetTitle } from '@cocoonspace/ui/components/sheet'
import { useLingui } from '@lingui/react/macro'
import { MailIcon, FilesIcon } from 'lucide-react'

import { LinkedinIcon } from './icons/linkedin'
import { TwitterIcon } from './icons/twitter'
import { WhatsAppIcon } from './icons/whatsapp'

const BaseShareBtn = ({
	label,
	Icon,
	onClick,
	disabled,
}: {
	label: string
	Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
	disabled?: boolean
	onClick: () => void
}) => (
	<button
		type='button'
		className='group flex w-full items-center gap-2 rounded-lg p-2 pr-4 transition-all duration-300 hover:bg-slate-100'
		onClick={onClick}
		disabled={disabled}
	>
		<div className='group-hover:bg-primary group-hover:text-primary-foreground flex size-10 items-center justify-center rounded-full bg-slate-100 transition-all duration-300'>
			<Icon className='size-5' />
		</div>

		<span className='text-sm text-gray-500'>{label}</span>
	</button>
)

const CopyToClipboard = ({ url }: { url: string }) => {
	const { t } = useLingui()
	const { copyToClipboard, isCopied } = useCopyClipboard()

	return (
		<BaseShareBtn
			label={isCopied ? t`Lien copié !` : t`Copier le lien`}
			Icon={FilesIcon}
			onClick={() => copyToClipboard(url)}
		/>
	)
}

const ShareOnWhatsApp = ({ txt, url }: { txt: string; url: string }) => {
	const { t } = useLingui()

	return (
		<BaseShareBtn
			label={t`Envoyer par WhatsApp`}
			Icon={WhatsAppIcon}
			onClick={() => {
				const body = [txt, url].join('\n\n')

				window.open(`https://wa.me/?text=${encodeURIComponent(body)}`, '_blank')
			}}
		/>
	)
}

const ShareByEmail = ({
	subject,
	txt,
	url,
}: {
	subject: string
	txt: string
	url: string
}) => {
	const { t } = useLingui()

	return (
		<BaseShareBtn
			label={t`Envoyer par email`}
			Icon={MailIcon}
			onClick={() => {
				const bodyTxt = [txt, url].join('\n\n')

				window.open(
					`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyTxt)}`,
					'_blank',
				)
			}}
		/>
	)
}

const ShareOnLinkedin = ({ txt, url }: { txt: string; url: string }) => {
	const { t } = useLingui()

	return (
		<BaseShareBtn
			label={t`Partager sur LinkedIn`}
			Icon={LinkedinIcon}
			onClick={() => {
				const body = [txt, url].join('\n\n')

				window.open(
					`https://www.linkedin.com/sharing/share-offsite/?text=${encodeURIComponent(body)}`,
					'_blank',
				)
			}}
		/>
	)
}

const ShareOnTwitter = ({ txt, url }: { txt: string; url: string }) => {
	const { t } = useLingui()

	return (
		<BaseShareBtn
			label={t`Partager sur Twitter`}
			Icon={TwitterIcon}
			onClick={() => {
				const body = [txt, url].join('\n\n')

				window.open(
					`https://twitter.com/intent/tweet?text=${encodeURIComponent(body)}`,
					'_blank',
				)
			}}
		/>
	)
}

export const ShareSheetContent = ({ title }: { title?: string }) => {
	const { t } = useLingui()
	const { data: profile } = useGetMeProfile()

	const body = profile?.title
	const url = `${window.location.origin}/${profile?.slug}`

	return (
		<div className='flex flex-col gap-2'>
			<div className='mx-2 text-lg font-bold'>{title ?? t`Partager`}</div>

			<div className='flex flex-col gap-2'>
				<CopyToClipboard url={url} />

				<ShareByEmail
					subject=''
					txt={body as string}
					url={url}
				/>

				<ShareOnWhatsApp
					txt={body as string}
					url={url}
				/>

				<ShareOnLinkedin
					txt={body as string}
					url={url}
				/>

				<ShareOnTwitter
					txt={body as string}
					url={url}
				/>
			</div>
		</div>
	)
}

export const ShareSheet = ({
	children,
	title,
}: {
	children: React.ReactNode
	title?: string
}) => {
	return (
		<MobileDrawer trigger={children}>
			<SheetTitle>{title}</SheetTitle>
			<ShareSheetContent title={title} />
		</MobileDrawer>
	)
}
